// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Link } from '@/components/link';
import { WarningStatusIcon } from '@/constants/hardfin-standard-icons';
import { pluralize } from '@/utilities/pluralize';
import { Chip, Tooltip } from '@mui/material';
import { MouseEvent } from 'react';

// The shipment detail route's tab param; typed so the literal is not widened.
const QUARANTINE_TAB: 'quarantine' = 'quarantine';

interface Props {
  // Open (unresolved) quarantined units this chip warns about
  openCount: number;

  // When set, the chip links to that shipment's quarantine tab
  shipmentKey?: string;
}

/**
 * Warning chip for open quarantined units. Renders nothing when there are
 * no open holds.
 */
export function QuarantineShipmentChip(props: Props) {
  if (props.openCount === 0) {
    return null;
  }

  const label = pluralize(props.openCount, {
    singular: 'unit in quarantine',
    plural: 'units in quarantine',
  });

  if (props.shipmentKey === undefined) {
    return (
      <Tooltip
        arrow
        title="Resolve these units on the shipment's quarantine tab"
      >
        <Chip
          size="small"
          color="warning"
          icon={<WarningStatusIcon />}
          label={label}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip arrow title="Resolve these units on the shipment's quarantine tab">
      <Link
        to="/shipments/$shipmentKey"
        params={{ shipmentKey: props.shipmentKey }}
        search={(prev) => ({ ...prev, tab: QUARANTINE_TAB })}
        underline="none"
        // The chip sits inside clickable rows; the row must not also navigate.
        onClick={(event: MouseEvent<HTMLAnchorElement>) =>
          event.stopPropagation()
        }
      >
        <Chip
          size="small"
          color="warning"
          clickable
          icon={<WarningStatusIcon />}
          label={label}
        />
      </Link>
    </Tooltip>
  );
}
